import { AdminQuotesPage, AdminQuotePage } from './CustomQuotesAdmin';
import { Fragment, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, LogOut, Search, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import type {
  ReferralAdminPartnerDetail,
  ReferralAdminPartnerRow,
  ReferralProgram,
} from '@zeroth/referral-contract';
import { errorMessage } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { cn, money } from '../lib/utils';
import { Button } from '../components/Button';
import { ThemeToggle } from '../components/ThemeToggle';
import zerothLogo from '../assets/zeroth-logo.png';
import { percent, tierFor } from './portalContext';
import { Card, CardTitle, PageHeader, Stat, shortDate, statusLabel } from './PartnerPortalPages';
import { useProgram } from './usePartnerDashboard';
import { isStaffOnly, useAdminOverview, useAdminPartner, useRecordPayout } from './useReferralAdmin';

/** Staff view at /admin. It never enrolls the viewer as a partner, unlike the partner dashboard. */
export function AdminPortal() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  if (!isLoaded) return <Centered><Spinner /></Centered>;
  if (!isSignedIn) return <Navigate to={'/sign-in?next=' + encodeURIComponent(location.pathname.startsWith('/admin/quotes') ? location.pathname : 'admin')} replace />;
  return (
    <AdminShell>
      <Routes>
        <Route index element={<AdminOverviewPage />} />
        <Route path="quotes" element={<AdminQuotesPage />} />
        <Route path="quotes/:id" element={<AdminQuotePage />} />
        <Route path="partners/:id" element={<AdminPartnerPage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminShell>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">{children}</div>;
}

function Spinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"
    />
  );
}

function AdminShell({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  return (
    <div className="min-h-[var(--app-viewport-height)] bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/admin" className="flex items-center gap-2.5">
            <img src={zerothLogo} alt="" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <p className="text-base font-semibold tracking-tight">Zeroth</p>
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Zeroth admin</p>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden max-w-[220px] truncate text-sm text-muted-foreground sm:block">
              {user?.primaryEmailAddress?.emailAddress}
            </span>
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              aria-label="Sign out"
              onClick={() => void signOut({ redirectUrl: window.location.origin })}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>
      <nav aria-label="Admin sections" className="mx-auto flex max-w-7xl gap-5 px-4 pt-5 text-sm sm:px-6 lg:px-8"><Link to="/admin" className="hover:underline">Partners</Link><Link to="/admin/quotes" className="hover:underline">Custom quotes</Link></nav>
      <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
    </div>
  );
}

/** Loading, staff-only and error states shared by both admin pages. */
function AdminQueryState({ query }: { query: { isError: boolean; error: unknown; refetch: () => unknown } }) {
  if (!query.isError) return <Centered><Spinner /></Centered>;
  if (isStaffOnly(query.error)) {
    return (
      <Centered>
        <ShieldCheck className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">This page is for Zeroth staff</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          Your account doesn’t have access to partner admin. Your own partner dashboard is still available.
        </p>
        <Link to="/" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
          Go to your partner dashboard
        </Link>
      </Centered>
    );
  }
  return (
    <Centered>
      <h1 className="text-xl font-semibold">Partner admin didn’t load</h1>
      <p role="alert" className="max-w-sm text-sm text-muted-foreground">
        {errorMessage(query.error)} Nothing was changed.
      </p>
      <Button variant="outline" onClick={() => void query.refetch()}>
        Try again
      </Button>
    </Centered>
  );
}

const methodName = (program: ReferralProgram | undefined, method: string | null) =>
  program?.payouts.methods.find((m) => m.id === method)?.name ?? method ?? '';

function Pill({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={cn('inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', tone)}>
      {children}
    </span>
  );
}

const PARTNER_STATUS: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
};
function PartnerStatus({ status }: { status: string }) {
  return (
    <Pill tone={PARTNER_STATUS[status] ?? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </Pill>
  );
}

function TierPill({ program, rate }: { program: ReferralProgram | undefined; rate: number }) {
  return (
    <Pill tone="bg-primary/10 text-primary">
      {program ? `${tierFor(program, rate).name} · ` : ''}
      {percent(rate)}
    </Pill>
  );
}

function PartnerName({ partner, to }: { partner: ReferralAdminPartnerRow; to?: string }) {
  const name = partner.name || partner.email;
  return (
    <div className="min-w-0">
      {to ? (
        <Link to={to} className="font-medium text-foreground hover:text-primary hover:underline">
          {name}
        </Link>
      ) : (
        <p className="font-medium">{name}</p>
      )}
      {partner.name && <p className="truncate text-xs text-muted-foreground">{partner.email}</p>}
    </div>
  );
}

function PaysTo({ partner, program }: { partner: ReferralAdminPartnerRow; program: ReferralProgram | undefined }) {
  if (!partner.payoutMethod || !partner.payoutHandle) {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> No payout details yet
      </span>
    );
  }
  return (
    <span>
      <span className="text-muted-foreground">{methodName(program, partner.payoutMethod)}</span>{' '}
      <span className="font-mono text-xs">{partner.payoutHandle}</span>
    </span>
  );
}

const TH = 'py-2.5 pr-4 font-medium';
const TABLE_HEAD = 'border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground';
const ROW = 'border-b border-border last:border-0';

function AdminOverviewPage() {
  useDocumentTitle('Partner admin');
  const overview = useAdminOverview();
  const { data: program } = useProgram();
  const [search, setSearch] = useState('');
  const partners = overview.data?.partners;
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!partners || !needle) return partners ?? [];
    return partners.filter((p) =>
      [p.name, p.email, p.referralUrl, p.payoutHandle].some((v) => v?.toLowerCase().includes(needle))
    );
  }, [partners, search]);
  if (!overview.data) return <AdminQueryState query={overview} />;
  const { totals, minimumPayoutCents } = overview.data;
  const due = overview.data.partners
    .filter((p) => p.payoutDue)
    .sort((a, b) => b.availableCents - a.availableCents);
  const partnerPath = (p: ReferralAdminPartnerRow) => `/admin/partners/${p.id}`;
  return (
    <>
      <PageHeader eyebrow="Zeroth staff" title="Partners & payouts">
        Every partner, who they’ve referred, and what you owe them. Balances match what each partner sees in their own
        dashboard.
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Owed now"
          value={money(totals.dueCents)}
          note={`${totals.duePartners} ${totals.duePartners === 1 ? 'partner' : 'partners'} at ${money(minimumPayoutCents)}+`}
        />
        <Stat
          label="Held"
          value={money(totals.pendingCents)}
          note={program ? `Still in the ${program.payouts.holdDays}-day hold` : 'Still in the hold period'}
        />
        <Stat label="Paid out" value={money(totals.paidCents)} note={`${money(totals.earnedCents)} earned in total`} />
        <Stat
          label="Referred sellers"
          value={String(totals.referrals)}
          note={`${totals.paying} paying · ${totals.partners} ${totals.partners === 1 ? 'partner' : 'partners'}`}
        />
      </div>

      <Card className="mt-6">
        <CardTitle title="Payouts due" />
        {due.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody is due a payout yet. Partners show up here once they’re active with at least{' '}
            {money(minimumPayoutCents)} past the hold.
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto px-5">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className={TH}>Partner</th>
                  <th className={TH}>Pays to</th>
                  <th className={cn(TH, 'text-right')}>Owed</th>
                  <th className="py-2.5 text-right font-medium">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {due.map((p) => (
                  <tr key={p.id} className={ROW}>
                    <td className="py-3 pr-4">
                      <PartnerName partner={p} />
                    </td>
                    <td className="py-3 pr-4">
                      <PaysTo partner={p} program={program} />
                    </td>
                    <td className="py-3 pr-4 text-right font-semibold tabular-nums">{money(p.availableCents)}</td>
                    <td className="py-3 text-right">
                      <Link
                        to={partnerPath(p)}
                        className="inline-flex h-9 items-center rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        Record payout
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="mt-6">
        <CardTitle
          title="All partners"
          action={
            <label className="relative block w-full max-w-xs">
              <span className="sr-only">Search partners</span>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email or link"
                className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm"
              />
            </label>
          }
        />
        {overview.data.partners.length === 0 ? (
          <p className="text-sm text-muted-foreground">No partners have joined yet.</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">No partners match “{search.trim()}”.</p>
        ) : (
          <div className="-mx-5 overflow-x-auto px-5">
            <table className="w-full min-w-[920px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className={TH}>Partner</th>
                  <th className={TH}>Tier</th>
                  <th className={TH}>Status</th>
                  <th className={cn(TH, 'text-right')}>Clicks</th>
                  <th className={cn(TH, 'text-right')}>Referred</th>
                  <th className={cn(TH, 'text-right')}>Held</th>
                  <th className={cn(TH, 'text-right')}>Owed</th>
                  <th className="py-2.5 text-right font-medium">Paid out</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p.id} className={ROW}>
                    <td className="py-3 pr-4">
                      <PartnerName partner={p} to={partnerPath(p)} />
                    </td>
                    <td className="py-3 pr-4">
                      <TierPill program={program} rate={p.rateBasisPoints} />
                    </td>
                    <td className="py-3 pr-4">
                      <PartnerStatus status={p.status} />
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">{p.clicks}</td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      {p.referrals}
                      <span className="block text-xs text-muted-foreground">{p.paying} paying</span>
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">{money(p.pendingCents)}</td>
                    <td
                      className={cn('py-3 pr-4 text-right tabular-nums', p.payoutDue && 'font-semibold text-primary')}
                    >
                      {money(p.availableCents)}
                    </td>
                    <td className="py-3 text-right tabular-nums">{money(p.paidCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function AdminPartnerPage() {
  const { id = '' } = useParams();
  const detail = useAdminPartner(id);
  const { data: program } = useProgram();
  const partner = detail.data?.partner;
  useDocumentTitle(partner ? `${partner.name || partner.email} · Partner admin` : 'Partner admin');
  if (!detail.data || !partner) return <AdminQueryState query={detail} />;
  const { referrals, payouts } = detail.data;
  return (
    <>
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All partners
      </Link>
      <PageHeader eyebrow="Partner" title={partner.name || partner.email}>
        {partner.name ? `${partner.email} · ` : ''}Joined {shortDate(partner.joinedAt)}
      </PageHeader>
      <div className="-mt-3 mb-6 flex flex-wrap items-center gap-2 text-sm">
        <PartnerStatus status={partner.status} />
        <TierPill program={program} rate={partner.rateBasisPoints} />
        <span className="font-mono text-xs text-muted-foreground">{partner.referralUrl}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Owed now"
          value={money(partner.availableCents)}
          note={partner.payoutDue ? 'Ready to pay' : 'Below the payout minimum'}
        />
        <Stat label="Held" value={money(partner.pendingCents)} note="Not payable yet" />
        <Stat label="Paid out" value={money(partner.paidCents)} note={`${payouts.length} recorded`} />
        <Stat
          label="Referred sellers"
          value={String(partner.referrals)}
          note={`${partner.paying} paying · ${partner.clicks} link clicks`}
        />
      </div>
      {partner.adjustmentCents < 0 && (
        <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
          A refund or chargeback after a payout left a {money(-partner.adjustmentCents)} adjustment. It comes out of their
          future earnings.
        </p>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-6">
          <ReferralsCard referrals={referrals} />
          <PayoutHistory payouts={payouts} />
        </div>
        <RecordPayoutCard detail={detail.data} program={program} />
      </div>
    </>
  );
}

function ReferralsCard({ referrals }: { referrals: ReferralAdminPartnerDetail['referrals'] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card>
      <CardTitle title="Referred sellers" />
      {referrals.length === 0 ? (
        <p className="text-sm text-muted-foreground">No sellers have signed up through this partner’s link yet.</p>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className={TABLE_HEAD}>
                <th className={TH}>Seller account</th>
                <th className={TH}>Joined</th>
                <th className={TH}>Subscription</th>
                <th className="py-2.5 text-right font-medium">Commission</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((ref) => {
                const status = statusLabel(ref.subscriptionStatus);
                const expanded = open === ref.id;
                const Icon = expanded ? ChevronDown : ChevronRight;
                return (
                  <Fragment key={ref.id}>
                    <tr className={ROW}>
                      <td className="py-3 pr-4">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          disabled={ref.commissions.length === 0}
                          onClick={() => setOpen(expanded ? null : ref.id)}
                          className="flex items-start gap-1.5 text-left disabled:cursor-default"
                        >
                          <Icon
                            className={cn(
                              'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground',
                              ref.commissions.length === 0 && 'invisible'
                            )}
                            aria-hidden="true"
                          />
                          <span className="min-w-0">
                            <span className="block font-medium">{ref.accountName}</span>
                            {ref.ownerEmail && (
                              <span className="block truncate text-xs text-muted-foreground">{ref.ownerEmail}</span>
                            )}
                          </span>
                        </button>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">{shortDate(ref.joinedAt)}</td>
                      <td className="py-3 pr-4">
                        <Pill tone={status.tone}>{status.label}</Pill>
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        {money(ref.earnedCents)}
                        <span className="block text-xs text-muted-foreground">
                          {ref.commissions.length} {ref.commissions.length === 1 ? 'payment' : 'payments'}
                        </span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className={ROW}>
                        <td colSpan={4} className="bg-muted/40 px-4 py-3">
                          <CommissionList commissions={ref.commissions} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CommissionList({ commissions }: { commissions: ReferralAdminPartnerDetail['referrals'][number]['commissions'] }) {
  const now = Date.now();
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left uppercase tracking-wider text-muted-foreground">
          <th className="pb-2 pr-3 font-medium">Paid</th>
          <th className="pb-2 pr-3 text-right font-medium">Seller paid</th>
          <th className="pb-2 pr-3 text-right font-medium">Rate</th>
          <th className="pb-2 pr-3 text-right font-medium">Commission</th>
          <th className="pb-2 font-medium">Payable</th>
        </tr>
      </thead>
      <tbody>
        {commissions.map((c) => (
          <tr key={c.invoiceId}>
            <td className="py-1 pr-3">{shortDate(c.paidAt)}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{money(c.eligibleCents)}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{percent(c.rateBasisPoints)}</td>
            <td
              className={cn('py-1 pr-3 text-right tabular-nums', c.commissionCents < 0 && 'text-destructive')}
            >
              {money(c.commissionCents)}
            </td>
            <td className="py-1 text-muted-foreground">
              {new Date(c.availableAt).getTime() <= now ? 'Now' : `From ${shortDate(c.availableAt)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayoutHistory({ payouts }: { payouts: ReferralAdminPartnerDetail['payouts'] }) {
  return (
    <Card>
      <CardTitle title="Payout history" />
      {payouts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No payouts recorded yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {payouts.map((payout) => (
            <li key={payout.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium">{shortDate(payout.paidAt)}</p>
                <p className="break-all text-xs text-muted-foreground">
                  Ref {payout.reference} · recorded by {payout.actorId}
                </p>
              </div>
              <p className="font-semibold tabular-nums">{money(payout.amountCents)}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Dollars typed by staff to integer cents, or null when it isn't a plain amount. */
export function parseDollars(value: string): number | null {
  const match = /^\$?\s*(\d{1,7})(?:\.(\d{1,2}))?$/.exec(value.trim().replace(/,/g, ''));
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
}

function RecordPayoutCard({
  detail,
  program,
}: {
  detail: ReferralAdminPartnerDetail;
  program: ReferralProgram | undefined;
}) {
  const { partner } = detail;
  const record = useRecordPayout(partner.id);
  const [amount, setAmount] = useState(() => (partner.availableCents / 100).toFixed(2));
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [recorded, setRecorded] = useState<number | null>(null);
  const minimum = program?.payouts.minimumCents;
  const cents = parseDollars(amount);
  const problem =
    cents === null
      ? 'Enter an amount in dollars, like 52.40.'
      : minimum !== undefined && cents < minimum
        ? `Payouts start at ${money(minimum)}.`
        : cents > partner.availableCents
          ? `That’s more than the ${money(partner.availableCents)} they’re owed.`
          : reference.trim().length < 3
            ? 'Add the transaction ID or note from the payment you sent.'
            : null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!problem) setConfirming(true);
  };
  const confirm = () =>
    record.mutate(
      { amountCents: cents!, reference: reference.trim() },
      {
        onSuccess: (next) => {
          setRecorded(cents);
          setConfirming(false);
          setReference('');
          setAmount((next.partner.availableCents / 100).toFixed(2));
        },
      }
    );
  const who = partner.name || partner.email;
  return (
    <Card className="order-first h-fit xl:sticky xl:top-24 xl:order-last">
      <CardTitle title="Record a payout" />
      {recorded !== null && (
        <p role="status" className="mb-4 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
          Recorded {money(recorded)}. It now shows in {who}’s payout history.
        </p>
      )}
      <dl className="mb-4 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Owed now</dt>
          <dd className="font-semibold tabular-nums">{money(partner.availableCents)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Pays to</dt>
          <dd className="text-right">
            <PaysTo partner={partner} program={program} />
          </dd>
        </div>
      </dl>
      {!partner.payoutDue ? (
        <p className="text-sm text-muted-foreground">
          {partner.status !== 'ACTIVE'
            ? `This partner is ${partner.status.toLowerCase()}, so payouts are paused.`
            : `Nothing to pay yet. Payouts start once at least ${money(minimum ?? 0)} is past the hold.`}
        </p>
      ) : confirming ? (
        <div className="space-y-4">
          <p className="text-sm">
            Record <strong>{money(cents!)}</strong> paid to <strong>{who}</strong>
            {partner.payoutMethod ? ` by ${methodName(program, partner.payoutMethod)}` : ''}, reference{' '}
            <span className="break-all font-mono text-xs">{reference.trim()}</span>?
          </p>
          <p className="text-xs text-muted-foreground">
            Only record money you’ve already sent. It comes off their balance and shows in their dashboard right away.
          </p>
          {record.isError && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(record.error)}
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={confirm} disabled={record.isPending}>
              {record.isPending ? 'Recording…' : 'Yes, record it'}
            </Button>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={record.isPending}>
              Go back
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-xs text-muted-foreground">
            Send the money first, then record it here with the payment’s reference.
          </p>
          <label className="block text-sm">
            <span className="font-medium">Amount sent (USD)</span>
            <input
              inputMode="decimal"
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setRecorded(null);
              }}
              className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm tabular-nums"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Payment reference</span>
            <input
              value={reference}
              maxLength={200}
              onChange={(event) => {
                setReference(event.target.value);
                setRecorded(null);
              }}
              placeholder="Transaction ID from PayPal, Venmo or Zelle"
              className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm"
            />
          </label>
          {problem && (amount || reference) && <p className="text-xs text-muted-foreground">{problem}</p>}
          <Button type="submit" className="w-full" disabled={!!problem}>
            Record payout
          </Button>
        </form>
      )}
    </Card>
  );
}
